import { useQuery } from "@apollo/client";
import { Avatar, Divider, DrawerBody, Text } from "@aurem/ui";
import { useAuthorize } from "graphql/user";
import { useNavigation } from "hooks/navigate";
import { useBasePath } from "hooks/useBasePath";
import {
  ActivityIcon,
  ChevronRightIcon,
  File,
  SettingsIcon,
} from "lucide-react";
import { useFeature } from "providers/FeaturesProvider";
import { useI18n } from "providers/I18nProvider";
import { Fragment, useCallback } from "react";
import { useParams } from "react-router-dom";
import { gql, PermissionEnum } from "types";

const GET_COMPANY = gql(/* GraphQL */ `
  query CompanyBottomNavDrawer($id: String!) {
    company: findCompany(id: $id) {
      id
      name
    }
  }
`);

type Props = {
  onClose: () => void;
};

export const MoreContent = ({ onClose }: Props) => {
  const { t } = useI18n();
  const navigate = useNavigation();
  const { path } = useBasePath();
  const { companyId = "" } = useParams();

  const { data } = useQuery(GET_COMPANY, {
    variables: { id: companyId },
    skip: !companyId,
  });

  const companyName = data?.company?.name ?? "";

  const canManageRequests = useAuthorize(
    PermissionEnum.ManageBenefit,
    companyId,
  );
  const canViewPayments = useAuthorize(
    PermissionEnum.ViewCompanyPayment,
    companyId,
  );
  const isActivityEnabled = useFeature("activity_feature");

  const getNavPath = useCallback(
    (navPath: string) => {
      return path(`companies/${companyId}/${navPath}`);
    },
    [path, companyId],
  );

  const handleNavigate = useCallback(
    (navPath: string) => {
      navigate(getNavPath(navPath));
      onClose();
    },
    [navigate, getNavPath, onClose],
  );

  const navigationItems = [
    {
      path: "requests",
      label: t("apps.admin.navBar.requests"),
      icon: File,
      show: canManageRequests,
    },
    {
      path: "activity-tracking",
      label: t("apps.admin.navBar.activity"),
      icon: ActivityIcon,
      show: isActivityEnabled && canViewPayments,
    },
    {
      path: "details",
      label: t("apps.admin.navBar.subNavBar.companySettings"),
      icon: SettingsIcon,
      show: true,
    },
  ];

  const visibleItems = navigationItems.filter((item) => item.show);

  return (
    <DrawerBody className="flex flex-col p-0 pointer-events-auto rounded-[1.25rem] bg-white shadow-2xl">
      <div className="flex items-center gap-3 p-4">
        <Avatar text={companyName} size="lg" />
        <Text size="md" weight="medium">
          {companyName}
        </Text>
      </div>

      <Divider direction="horizontal" />

      <nav className="flex flex-col p-6 pb-4 pt-2">
        {visibleItems.map((item, index) => {
          const Icon = item.icon;
          const isLast = index === visibleItems.length - 1;
          return (
            <Fragment key={item.path}>
              <button
                onClick={() => handleNavigate(item.path)}
                className="flex items-center gap-3 h-14 text-start"
              >
                <span className="flex items-center justify-center w-8 h-8 shrink-0 rounded-md bg-neutral-50">
                  <Icon
                    size="1.25rem"
                    className="rtl:-scale-x-100 text-neutral-200"
                  />
                </span>
                <Text size="md" className="text-neutral-500">
                  {item.label}
                </Text>
                <ChevronRightIcon className="rtl:-scale-x-100 ms-auto text-neutral-200" />
              </button>
              {!isLast && <Divider />}
            </Fragment>
          );
        })}
      </nav>
    </DrawerBody>
  );
};
